// useFCMNotifications.js
// Place in src/utils/useFCMNotifications.js  (replaces the current file)
//
// Mobile-only web push registration.
//
// Changes from the previous version:
//  1. saveTokenToDatabase sends Authorization: Bearer <Firebase ID token>
//     to m115-freshcard (required once the lockdown is deployed).
//  2. No automatic permission prompt on page load. The browser prompt is
//     only shown when the user taps the opt-in button (a real user gesture).
//  3. If permission is already 'granted', the token is silently refreshed
//     on load so tokens never go stale.
//  4. Save failures log the HTTP status instead of failing silently.
//
// Usage — App.js (unchanged):
//   <FCMInitialiser uid={userdata?.uid || userdata?.id} />
// Usage — add the button somewhere visible, e.g. the agent dashboard or the
// Profile window next to the outreach settings:
//   import { FCMOptInButton } from './utils/useFCMNotifications';
//   <FCMOptInButton uid={this.props.user?.uid} />

import React, { useEffect, useState, useCallback } from 'react';
import axios                from 'axios';
import firebaseApp          from '../config/fbConfig';
import { FB_URL }           from '../store/actions/types';
import { isMobileDevice }   from './deviceDetect';
import Button               from '@material-ui/core/Button';
import Typography           from '@material-ui/core/Typography';

const VAPID_KEY = "BMVa_m0YgukrZHaBdZ3BEF9Zy2qL2RRcRwYeA3ZuSmfD-r4Cja6jZo7z6CzAWTVW-5Enu5mNuqUdZ4fr5FIHizc";

// ---- Register the service worker ----
async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('FCM: service workers not supported');
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('FCM: service worker registered');
    return registration;
  } catch (err) {
    console.error('FCM: service worker registration failed:', err);
    return null;
  }
}

// ---- Firebase ID token for the m115 Authorization header ----
async function getIdToken() {
  try {
    const user = firebaseApp.auth().currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch (err) {
    console.error('FCM: could not get ID token:', err.message || err);
    return null;
  }
}

// ---- Save FCM token via m115-freshcard (authenticated) ----
async function saveTokenToDatabase(uid, token) {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('FCM: no signed-in user / ID token — token not saved');
      return false;
    }

    const tokenKey = token.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_');
    const ua = navigator.userAgent || '';

    const updates = {};
    updates[`user/${uid}/fcmTokens/${tokenKey}`] = {
      token:      token,
      updatedAt:  Date.now(),
      platform:   'web',
      deviceType: 'mobile',
      userAgent:  ua,
    };
    updates[`user/${uid}/fcmToken`]          = token;
    updates[`user/${uid}/fcmTokenUpdatedAt`] = Date.now();

    const res = await axios.post(
      `${FB_URL}/m115-freshcard`,
      { params: { updates } },
      {
        headers:        { Authorization: `Bearer ${idToken}` },
        validateStatus: () => true, // inspect status ourselves
      }
    );

    if (res.status === 200 && res.data && res.data.success === true) {
      console.log('FCM: token saved to database for uid', uid);
      return true;
    }
    console.error('FCM: token save rejected — status', res.status, res.data);
    return false;
  } catch (err) {
    console.error('FCM: failed to save token via m115-freshcard:', err.message);
    return false;
  }
}

// ---- Current state, for UI ----
// 'unsupported' | 'desktop' | 'blocked' | 'off' | 'on'
export function getFcmStatus() {
  if (!isMobileDevice()) return 'desktop';
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (Notification.permission === 'denied')  return 'blocked';
  if (Notification.permission === 'granted') return 'on';
  return 'off';
}

// ---- Get a token and save it. Never prompts on its own. ----
async function registerToken(uid, onForegroundMessage) {
  const swRegistration = await registerServiceWorker();
  if (!swRegistration) return false;

  const messaging = firebaseApp.messaging();
  const token = await messaging.getToken({
    vapidKey:                  VAPID_KEY,
    serviceWorkerRegistration: swRegistration,
  });

  if (!token) {
    console.log('FCM: no token returned — check VAPID key and service worker');
    return false;
  }
  console.log('FCM: token obtained successfully');

  const saved = await saveTokenToDatabase(uid, token);

  // Foreground messages: call showNotification() directly on the SW
  // registration (the compat SDK's automatic toast is unreliable on
  // Android Chrome).
  messaging.onMessage((payload) => {
    console.log('FCM foreground message received:', payload);
    const title = payload?.data?.title || 'ThinkLead';
    const body  = payload?.data?.body  || '';
    swRegistration.showNotification(title, {
      body,
      icon: '/logo192.png',
      data: payload.data,
    }).catch(err => console.error('FCM: foreground showNotification failed:', err));
    if (typeof onForegroundMessage === 'function') onForegroundMessage(payload);
  });

  return saved;
}

// ---- Silent refresh on load: only if the user already allowed notifications ----
export async function refreshFcmIfGranted(uid, onForegroundMessage) {
  if (!uid || getFcmStatus() !== 'on') return;
  try {
    await registerToken(uid, onForegroundMessage);
  } catch (err) {
    console.error('FCM: refresh error:', err.message || err);
  }
}

// ---- Called from a user tap: shows the browser prompt, then registers ----
// Returns the resulting status string.
export async function enableNotifications(uid, onForegroundMessage) {
  if (!uid) return getFcmStatus();
  const status = getFcmStatus();
  if (status === 'desktop' || status === 'unsupported' || status === 'blocked') return status;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return getFcmStatus();
    await registerToken(uid, onForegroundMessage);
  } catch (err) {
    console.error('FCM: enable error:', err.message || err);
  }
  return getFcmStatus();
}

// ---- Backwards-compatible export ----
// NotificationStatus.jsx imports initFCM. Same behaviour as before for that
// caller: shows the browser prompt (if needed) and registers the token.
// The only difference is that it is no longer fired automatically on page
// load by FCMInitialiser — it runs when NotificationStatus calls it.
export async function initFCM(uid, onForegroundMessage) {
  return enableNotifications(uid, onForegroundMessage);
}

// ---- Mounted once in App.js: silent refresh only, no prompt ----
export function FCMInitialiser({ uid, onForegroundMessage }) {
  useEffect(() => {
    if (uid && isMobileDevice()) {
      refreshFcmIfGranted(uid, onForegroundMessage);
    }
    }, [uid]);

  return null;
}

// ---- Opt-in button (mobile only) ----
export function FCMOptInButton({ uid, onForegroundMessage }) {
  const [status, setStatus] = useState(getFcmStatus());
  const [busy, setBusy]     = useState(false);

  const onTap = useCallback(async () => {
    setBusy(true);
    const next = await enableNotifications(uid, onForegroundMessage);
    setStatus(next);
    setBusy(false);
  }, [uid, onForegroundMessage]);

  if (!uid || status === 'desktop' || status === 'unsupported') return null;

  if (status === 'on') {
    return (
      <Typography variant="caption" color="textSecondary">
        Notifications are on for this device.
      </Typography>
    );
  }

  if (status === 'blocked') {
    return (
      <Typography variant="caption" color="textSecondary">
        Notifications are blocked for ThinkLead. To get nudges from your agents,
        allow notifications for this site in your browser settings.
      </Typography>
    );
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      disabled={busy}
      onClick={onTap}
    >
      {busy ? 'Turning on…' : 'Turn on agent nudges'}
    </Button>
  );
}